import { deleteSkills, putSkills } from "@/common/character/skills";

import {
  deleteWeaponSkills,
  putWeaponSkills,
} from "@/common/character/weaponSkills";

export interface SkillsStore {
  purchasedWeaponSkills: Array<any>; // Get this locally from rank
  purchasedSkills: Array<any>; // Get this locally from rank
  weaponSkills: WeaponSkills;
  skills: BaseSkillsStore;
}

export interface BaseSkillsStore {
  acrobatics: SkillsItemStore;
  alchemy: SkillsItemStore;
  awareness: SkillsItemStore;
  crafting: SkillsItemStore;
  fitness: SkillsItemStore;
  interpersonal: SkillsItemStore;
  knowledge: SkillsItemStore;
  metamagic: SkillsItemStore;
  might: SkillsItemStore;
  performance: SkillsItemStore;
  subtlety: SkillsItemStore;
}
export interface WeaponSkills {
  precision: SkillsItemStore;
  versatile: SkillsItemStore;
  heavy: SkillsItemStore;
  axes: SkillsItemStore;
  brawling: SkillsItemStore;
  hammers: SkillsItemStore;
  knives: SkillsItemStore;
  polearms: SkillsItemStore;
  swords: SkillsItemStore;
  whips: SkillsItemStore;
  bows: SkillsItemStore;
  crossbows: SkillsItemStore;
  handguns: SkillsItemStore;
  machineGuns: SkillsItemStore;
  rifles: SkillsItemStore;
  shotguns: SkillsItemStore;
  customWeapon: SkillsItemStore;
}
export interface SkillsItemStore {
  id: number;
  name: string;
  rank: number;
}

export default {
  namespaced: true,
  state: (): SkillsStore => ({
    purchasedWeaponSkills: [], // Get this locally from rank
    purchasedSkills: [], // Get this locally from rank
    weaponSkills: {
      precision: { id: 0, name: "Precision", rank: 0 },
      versatile: { id: 0, name: "Versatile", rank: 0 },
      heavy: { id: 0, name: "Heavy", rank: 0 },
      axes: { id: 0, name: "Axes", rank: 0 },
      brawling: { id: 0, name: "Brawling", rank: 0 },
      hammers: { id: 0, name: "Hammers", rank: 0 },
      knives: { id: 0, name: "Knives", rank: 0 },
      polearms: { id: 0, name: "Polearms", rank: 0 },
      swords: { id: 0, name: "Swords", rank: 0 },
      whips: { id: 0, name: "Whips", rank: 0 },
      bows: { id: 0, name: "Bows", rank: 0 },
      crossbows: { id: 0, name: "Crossbows", rank: 0 },
      handguns: { id: 0, name: "Handguns", rank: 0 },
      machineGuns: { id: 0, name: "MachineGuns", rank: 0 },
      rifles: { id: 0, name: "Rifles", rank: 0 },
      shotguns: { id: 0, name: "Shotguns", rank: 0 },
      customWeapon: { id: 0, name: "Custom Weapon", rank: 0 },
    },
    skills: {
      acrobatics: { id: 0, name: "Acrobatics", rank: 0 },
      alchemy: { id: 0, name: "Alchemy", rank: 0 },
      awareness: { id: 0, name: "Awareness", rank: 0 },
      crafting: { id: 0, name: "Crafting", rank: 0 },
      fitness: { id: 0, name: "Fitness", rank: 0 },
      interpersonal: { id: 0, name: "Interpersonal", rank: 0 },
      knowledge: { id: 0, name: "Knowledge", rank: 0 },
      metamagic: { id: 0, name: "Metamagic", rank: 0 },
      might: { id: 0, name: "Might", rank: 0 },
      performance: { id: 0, name: "Performance", rank: 0 },
      subtlety: { id: 0, name: "Subtlety", rank: 0 },
    },
  }),
  getters: {
    weaponSkills: (state: SkillsStore) => {
      return state.weaponSkills;
    },
    skills: (state: SkillsStore) => {
      return state.skills;
    },
    getSkills: (state: SkillsStore) => {
      return {
        purchasedWeaponSkills: state.purchasedWeaponSkills,
        purchasedSkills: state.purchasedSkills,
        weaponSkills: state.weaponSkills,
        skills: state.skills,
      };
    },
    getMp: (state: SkillsStore) => {
      let ret = 0;
      for (const skill in state.weaponSkills) {
        ret = Math.max(
          state.weaponSkills[skill as keyof SkillsStore["weaponSkills"]].rank,
          ret
        );
      }
      return ret;
    },
    getPurchasedWeaponSkills: (state: SkillsStore) => {
      const ret = [];
      for (const weaponSkill in state.weaponSkills) {
        if (
          state.weaponSkills[weaponSkill as keyof SkillsStore["weaponSkills"]]
            .rank > 0
        ) {
          ret.push(
            state.weaponSkills[weaponSkill as keyof SkillsStore["weaponSkills"]]
          );
        }
      }
      return ret;
    },
    getSpecificSkillRank: (state: SkillsStore, weaponSkill: string) => {
      return state.weaponSkills[
        weaponSkill as keyof SkillsStore["weaponSkills"]
      ].rank;
    },
    getPrecision: (state: SkillsStore) => {
      return state.weaponSkills.precision.rank;
    },
    getVersatile: (state: SkillsStore) => {
      return state.weaponSkills.versatile.rank;
    },
    getHeavy: (state: SkillsStore) => {
      return state.weaponSkills.heavy.rank;
    },
    getAxes: (state: SkillsStore) => {
      return state.weaponSkills.axes.rank;
    },
    getBrawling: (state: SkillsStore) => {
      return state.weaponSkills.brawling.rank;
    },
    getHammers: (state: SkillsStore) => {
      return state.weaponSkills.hammers.rank;
    },
    getKnives: (state: SkillsStore) => {
      return state.weaponSkills.knives.rank;
    },
    getPolearms: (state: SkillsStore) => {
      return state.weaponSkills.polearms.rank;
    },
    getSwords: (state: SkillsStore) => {
      return state.weaponSkills.swords.rank;
    },
    getWhips: (state: SkillsStore) => {
      return state.weaponSkills.whips.rank;
    },
    getBows: (state: SkillsStore) => {
      return state.weaponSkills.bows.rank;
    },
    getCrossbows: (state: SkillsStore) => {
      return state.weaponSkills.crossbows.rank;
    },
    getHandguns: (state: SkillsStore) => {
      return state.weaponSkills.handguns.rank;
    },
    getMachineGuns: (state: SkillsStore) => {
      return state.weaponSkills.machineGuns.rank;
    },
    getRifles: (state: SkillsStore) => {
      return state.weaponSkills.rifles.rank;
    },
    getShotguns: (state: SkillsStore) => {
      return state.weaponSkills.shotguns.rank;
    },
    getCustomWeapon: (state: SkillsStore) => {
      return state.weaponSkills.customWeapon.rank;
    },
  },
  mutations: {
    setWeaponSkills: (state: SkillsStore, newData: WeaponSkills) => {
      state.weaponSkills = newData;
    },
    setSkills: (state: SkillsStore, newData: BaseSkillsStore) => {
      state.skills = newData;
    },
    setWeaponSkill: (state: SkillsStore, newData: SkillsItemStore) => {
      const altered = {
        rank: newData.rank,
        name:
          newData.name.substring(0, 1).toUpperCase() +
          newData.name.substring(1),
        id: newData.id,
      };
      state.weaponSkills[newData.name as keyof SkillsStore["weaponSkills"]] =
        altered;
    },
    setSkill: (state: SkillsStore, newData: SkillsItemStore) => {
      const altered = {
        rank: newData.rank,
        name:
          newData.name.substring(0, 1).toUpperCase() +
          newData.name.substring(1),
        id: newData.id,
      };
      state.skills[newData.name as keyof SkillsStore["skills"]] = altered;
    },
    setPurchasedWeaponSkills: (state: SkillsStore, newData: Array<any>) => {
      state.purchasedWeaponSkills = newData;
    },
    setPurchasedSkills: (state: SkillsStore, newData: Array<any>) => {
      state.purchasedSkills = newData;
    },
    clearSkills: (state: SkillsStore) => {
      const arr = Object.keys(state.skills);
      for (const skill of arr) {
        state.skills[skill as keyof SkillsStore["skills"]].rank = 0;
      }
    },
    clearWeaponSkills: (state: SkillsStore) => {
      const arr = Object.keys(state.weaponSkills);
      for (const skill of arr) {
        state.weaponSkills[skill as keyof SkillsStore["weaponSkills"]].rank = 0;
      }
    },
  },
  actions: {
    updateSkill: (
      context: any,
      newData: {
        rank: number;
        name: string;
        character_id: string;
        id: number;
      }
    ) => {
      if (
        context.getters.skills[newData.name as keyof SkillsStore["skills"]]
          .rank === 0
      ) {
        if (newData.rank > 0) {
          putSkills(
            newData.character_id,
            newData.name,
            newData.rank,
            newData.id
          ).then((response) => {
            context.commit("setSkill", {
              rank: newData.rank,
              name: newData.name,
              id: response.data[0].id,
            });
          });
        }
      } else {
        if (newData.rank === 0) {
          deleteSkills(
            newData.character_id,
            newData.name,
            context.getters.skills[newData.name as keyof SkillsStore["skills"]]
              .rank,
            newData.id
          );
        } else if (newData.rank >= 0) {
          putSkills(
            newData.character_id,
            newData.name,
            newData.rank,
            newData.id
          );
        }
        context.commit("setSkill", {
          rank: newData.rank,
          name: newData.name,
          id: newData.id,
        });
      }
    },
    updateWeaponSkill: (
      context: any,
      newData: {
        id: number;
        rank: number;
        name: string;
        character_id: string;
      }
    ) => {
      if (
        context.getters.weaponSkills[
          newData.name as keyof SkillsStore["weaponSkills"]
        ].rank === 0
      ) {
        if (newData.rank > 0) {
          putWeaponSkills(
            newData.character_id,
            newData.name,
            newData.rank,
            newData.id
          ).then((response) => {
            context.commit("setWeaponSkill", {
              rank: newData.rank,
              name: newData.name,
              id: response.data[0].id,
            });
          });
        }
      } else {
        if (newData.rank === 0) {
          deleteWeaponSkills(
            newData.character_id,
            newData.name,
            context.getters.weaponSkills[
              newData.name as keyof SkillsStore["weaponSkills"]
            ].rank,
            newData.id
          );
        } else if (newData.rank >= 0) {
          putWeaponSkills(
            newData.character_id,
            newData.name,
            newData.rank,
            newData.id
          );
        }
        context.commit("setWeaponSkill", {
          rank: newData.rank,
          name: newData.name,
          id: newData.id,
        });
      }
    },
  },
};
